import axios from 'axios';

import {
  LOGISTIC_LOCAL_BASE_URL,
  isLogisticLocalEnabled,
} from '@/config/logistic-api';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { storage } from '@/utils/storage';

/**
 * Scheduled Instant can target a different backend than the rest of the app —
 * its endpoints ship separately. Set `REACT_APP_SI_API_URL` (and optionally
 * `REACT_APP_SI_TOKEN`) to pin SI calls to a specific BE. When that env is
 * unset it follows the same "point logistic at localhost" toggle as every
 * other logistic path, and otherwise falls back to the shared API base with
 * the normal localStorage token.
 *
 * A dedicated instance (not the shared Axios) means a local SI hiccup never
 * trips the app-wide 401 → sign-out interceptor. That isolation is why the
 * shared instance's local-override interceptor doesn't reach these calls, so
 * the same rule is applied here explicitly — otherwise SI silently keeps
 * hitting the gateway while the rest of the app reads localhost, and the
 * calendar shows a different environment's data than the dispatch board.
 */
const SI_PINNED_BASE_URL = process.env.REACT_APP_SI_API_URL || '';
const SI_FALLBACK_BASE_URL = process.env.REACT_APP_API_URL || '';

/** Resolved per request — the local toggle is flipped at runtime. */
const siBaseUrl = (): string => {
  if (SI_PINNED_BASE_URL) return SI_PINNED_BASE_URL;
  return isLogisticLocalEnabled()
    ? LOGISTIC_LOCAL_BASE_URL
    : SI_FALLBACK_BASE_URL;
};

const Axios = axios.create({
  headers: { 'Content-Type': 'application/json', 'dash-client-type': 'web' },
});

Axios.interceptors.request.use((config) => {
  config.baseURL = siBaseUrl();
  const token =
    process.env.REACT_APP_SI_TOKEN ||
    storage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export type SiConfirmationStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'NO_RESPONSE'
  | 'NEEDS_REPLACEMENT'
  | 'REPLACED'
  | 'COMPLETED'
  | 'FAILED';

export type SiSlotRiderRole = 'utama' | 'cadangan';

export type SiSlotStatusFilter =
  | 'has_no_response'
  | 'has_pending'
  | 'all_confirmed';

export type SiAggregateStatus =
  | 'confirmed'
  | 'pending'
  | 'no_response'
  | 'unstaffed';

export interface SiSlot {
  id: string;
  scheduleId: string | null;
  providerId: number;
  clientName: string;
  scheduleDate: string;
  windowStartTime: string;
  windowEndTime: string;
  batchLabel: string | null;
  notes: string | null;
  /** ISO time this dated slot was manually overridden via Edit Slot; null = never. */
  editedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiSlotRider {
  id: string;
  riderCode: string;
  riderName: string;
  riderPhone: string;
  role: SiSlotRiderRole;
  confirmationStatus: SiConfirmationStatus;
  replacedById: string | null;
  isDayOff: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiStatusCounts {
  green: number;
  amber: number;
  red: number;
}

export interface SiSlotSummary {
  slot: SiSlot;
  riderCount: number;
  counts: SiStatusCounts;
  aggregateStatus: SiAggregateStatus;
  dayOffCount: number;
  /** UTAMA rider names only — for at-a-glance calendar display (cadangan in detail). */
  riderNames: string[];
}

export interface SiSlotActivity {
  id: string;
  slotRiderId: string | null;
  action: string;
  fromStatus: string | null;
  toStatus: string | null;
  reason: string | null;
  actor: string;
  createdAt: string;
}

export interface SiSlotDetail {
  slot: SiSlot;
  mainRiders: SiSlotRider[];
  reserveRiders: SiSlotRider[];
  counts: SiStatusCounts;
  activity: SiSlotActivity[];
}

export type SiStandbyReason = 'DAY_OFF' | 'CONFLICT' | null;

export interface SiStandbyAvailability {
  riderCode: string;
  available: boolean;
  reason: SiStandbyReason;
}

export interface SiSchedule {
  id: string;
  providerId: number;
  clientName: string;
  windowStartTime: string;
  windowEndTime: string;
  recurringWeekdays: number[];
  batchLabel: string | null;
  toleranceTier: string;
  isActive: boolean;
  deactivatedEffectiveDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiScheduleRider {
  id: string;
  scheduleId: string;
  riderCode: string;
  riderName: string;
  riderPhone: string;
  role: SiSlotRiderRole;
  createdAt: string;
  updatedAt: string;
}

export interface SiScheduleRiderPreview {
  riderCode: string;
  riderName: string;
}

export interface SiScheduleRosterSummary {
  scheduleId: string;
  utamaCount: number;
  cadanganCount: number;
  utamaPreview: SiScheduleRiderPreview[];
  cadanganPreview: SiScheduleRiderPreview[];
}

export interface SiScheduleChange {
  id: string;
  changeType: string;
  reason: string | null;
  effectiveDate: string | null;
  actor: string;
  createdAt: string;
}

export interface SiScheduleDetail {
  schedule: SiSchedule;
  changes: SiScheduleChange[];
  utamaRiders: SiScheduleRider[];
  cadanganRiders: SiScheduleRider[];
}

export interface SiRiderDayOff {
  riderCode: string;
  /** Resolved server-side from the SI roster; null → fall back to the code. */
  riderName: string | null;
  recurringWeekdays: number[] | null;
  specificDates: string[] | null;
  updatedAt: string;
}

export interface CreateSlotRiderInput {
  riderCode: string;
  riderName: string;
  riderPhone: string;
  role: SiSlotRiderRole;
}

export interface CreateSlotPayload {
  providerId: number;
  clientName: string;
  scheduleDate: string;
  windowStartTime: string;
  windowEndTime: string;
  batchLabel?: string | null;
  notes?: string | null;
  scheduleId?: string | null;
  riders: CreateSlotRiderInput[];
}

export interface CreateSchedulePayload {
  providerId: number;
  clientName: string;
  windowStartTime: string;
  windowEndTime: string;
  recurringWeekdays?: number[];
  batchLabel?: string | null;
  riders?: CreateSlotRiderInput[];
}

export interface UpdateSchedulePayload {
  providerId: number;
  clientName: string;
  windowStartTime: string;
  windowEndTime: string;
  recurringWeekdays: number[];
  batchLabel?: string | null;
  // Omit to edit time/weekdays only; provide to replace roster + cascade (D3).
  riders?: CreateSlotRiderInput[];
  reason?: string;
}

export interface UpdateSlotPayload {
  // Optional new date (YYYY-MM-DD) — moves this one slot only. Omit to keep.
  scheduleDate?: string;
  windowStartTime: string;
  windowEndTime: string;
  riders: CreateSlotRiderInput[];
  reason: string;
}

export interface ListSlotsParams {
  start_date: string;
  end_date: string;
  client?: string;
  status?: SiSlotStatusFilter;
  schedule_id?: string;
}


// --- Reliability metrics (Screen 14) -----------------------------------------

/**
 * Read `medianMinutes` and `resolvedRate` TOGETHER. The median only covers
 * escalations that were actually resolved, so abandoning a slot removes a slow
 * datapoint and makes the median look better. `resolvedRate` keeps every
 * escalation in its denominator, give-ups included.
 */
export interface SiMetricsReplacement {
  medianMinutes: number | null;
  p90Minutes: number | null;
  /** Resolved WITHIN targetMinutes / total — the strict rate. */
  resolvedRate: number | null;
  resolvedWithinTarget: number;
  resolved: number;
  total: number;
  targetMinutes: number;
  /** Plain resolved / total — the Framework's "Berhasil Dapat Pengganti". */
  recoveryRate: number | null;
  /** Same-length period immediately before, for the delta. Null = no data. */
  previousMedianMinutes: number | null;
  previousRecoveryRate: number | null;
}

/**
 * One row of the per-client table. Rates are null below the minimum slot count
 * (see CLIENT_METRIC_MIN_SLOTS server-side) — render "n/a", never 0%.
 */
export interface SiMetricsClient {
  clientName: string;
  totalSlots: number;
  medianMinutes: number | null;
  recoveryRate: number | null;
  escalated: number;
  resolved: number;
  noShowRateT12: number | null;
  noShowRateT2: number | null;
}

export interface SiMetricsCheckpoint {
  phase: 'T12' | 'T2';
  /** Messages that reached the rider (excludes FAILED, includes DELIVERED). */
  delivered: number;
  confirmed: number;
  declined: number;
  silent: number;
  responseRate: number | null;
}

export interface SiMetricsDay {
  scheduleDate: string;
  escalated: number;
  recovered: number;
  /** Escalated, never replaced, ended CONFIRMED — the rider came late. */
  confirmedLate: number;
  medianMinutes: number | null;
}

export interface SiMetrics {
  startDate: string;
  endDate: string;
  replacement: SiMetricsReplacement;
  checkpoints: SiMetricsCheckpoint[];
  /**
   * Whether the T-2 re-confirm ping is on (server SI_T2_ENABLED). When ops drop
   * T-2 for cost, hide the T-2 column rather than show an all-zero one.
   */
  t2Enabled: boolean;
  /** Riders whose confirmation never arrived — a bad number, not silence. */
  unreachableRiders: number;
  /** Escalations that resolved themselves (late YA, no replacement). */
  confirmedLate: number;
  silentNow: number;
  declinedNow: number;
  trend: SiMetricsDay[];
  /** Slots created in the period. */
  totalSlots: number;
  /** Per-client breakdown — always all clients, ignores the client filter. */
  perClient: SiMetricsClient[];
}

export interface MetricsParams {
  start_date: string;
  end_date: string;
  client?: string;
}

/** One rider behind the "Nomor Tidak Aktif" tile. */
export interface SiUnreachableRider {
  slotRiderId: string;
  slotId: string;
  riderCode: string;
  riderName: string;
  riderPhone: string | null;
  clientName: string;
  scheduleDate: string;
  windowStartTime: string;
  /** Which ping failed most recently (T12 / T2). */
  lastFailedPhase: string;
  /** When that ping was sent (ISO). */
  failedAt: string;
}

export interface SiUnreachableRiders {
  startDate: string;
  endDate: string;
  count: number;
  data: SiUnreachableRider[];
}

// --- Attention riders (Screen 15) --------------------------------------------

export interface SiAttentionRider {
  slotRiderId: string;
  slotId: string;
  riderCode: string;
  riderName: string;
  riderPhone: string;
  clientName: string;
  scheduleDate: string;
  windowStartTime: string;
  windowEndTime: string;
  confirmationStatus: 'NO_RESPONSE' | 'NEEDS_REPLACEMENT';
  lastPingPhase: 'T12' | 'T2' | null;
  lastPingAt: string | null;
  /** Silence started here — the UI derives elapsed time from this instant. */
  silentSinceAt: string | null;
  respondedAt: string | null;
  /** The confirmation never reached them. Not the same as ignoring it. */
  sendFailed: boolean;
}

export interface SiAttentionRiders {
  date: string;
  silent: SiAttentionRider[];
  declined: SiAttentionRider[];
  silentCount: number;
  declinedCount: number;
}

// --- Slot-vs-delivery reconciliation (Screen 14 "Rekonsiliasi" tab) -----------

/** A rider on a slot roster who completed no delivery that day. */
export interface SiReconAssignedRider {
  riderCode: string;
  riderName: string;
  /** Clients whose slots this rider was on. */
  clientNames: string[];
  /** Earliest slot window start ("HH:MM"). */
  earliestWindow: string;
}

/** A rider who completed deliveries but was on no slot roster that day. */
export interface SiReconCompletedRider {
  /** Courier name from delivery-service; equals the phone when unresolved. */
  riderName: string;
  /** Rider phone — the match key and only stable id for off-roster completers. */
  phone: string;
  completed: number;
}

/** Default (cheap) reconciliation — loaded on tab open. S\C only. */
export interface SiReconciliation {
  scheduleDate: string;
  assignedCount: number;
  matchedCount: number;
  assignedNoDelivery: SiReconAssignedRider[];
}

/** On-demand C\S — loaded when the user asks to see "delivered, not assigned". */
export interface SiReconciliationUnassigned {
  scheduleDate: string;
  completedCount: number;
  deliveredNoSlot: SiReconCompletedRider[];
  /** True when the completer list may be incomplete (hit the page cap). */
  truncated: boolean;
}

type Envelope<T> = { status: string; message?: string; data: T };
const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

const BASE = '/v1/scheduled-instant';

export const scheduledInstantAPI = {
  /** Screen 14 — reliability metrics over a slot-date range. */
  getMetrics: async (params: MetricsParams): Promise<SiMetrics> => {
    try {
      const res = await Axios.get(`${BASE}/metrics`, { params });
      return unwrap<SiMetrics>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** Drill-down for the "Nomor Tidak Aktif" tile — the riders behind the count. */
  listUnreachableRiders: async (
    params: MetricsParams
  ): Promise<SiUnreachableRiders> => {
    try {
      const res = await Axios.get(`${BASE}/unreachable-riders`, { params });
      return unwrap<SiUnreachableRiders>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Screen 14 "Rekonsiliasi" tab — slot assignments vs actual completions for
   * one day. Omit `date` to let the server use yesterday in Jakarta.
   */
  reconciliation: async (date?: string): Promise<SiReconciliation> => {
    try {
      const res = await Axios.get(`${BASE}/reconciliation`, {
        params: date ? { date } : {},
      });
      return unwrap<SiReconciliation>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * On-demand C\S ("Mengirim, tidak di-assign") — the heavy completer
   * enumeration, loaded only when the user asks to see it.
   */
  reconciliationUnassigned: async (
    date?: string
  ): Promise<SiReconciliationUnassigned> => {
    try {
      const res = await Axios.get(`${BASE}/reconciliation/unassigned`, {
        params: date ? { date } : {},
      });
      return unwrap<SiReconciliationUnassigned>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Screen 15 — riders needing attention on one date, split silent vs declined.
   * Omit `date` to let the server use today in Jakarta.
   */
  listAttentionRiders: async (params?: {
    date?: string;
    client?: string;
  }): Promise<SiAttentionRiders> => {
    try {
      const res = await Axios.get(`${BASE}/attention-riders`, { params });
      return unwrap<SiAttentionRiders>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  listSlots: async (params: ListSlotsParams): Promise<SiSlotSummary[]> => {
    try {
      const res = await Axios.get(`${BASE}/slots`, { params });
      return unwrap<{ slots: SiSlotSummary[] }>(res.data).slots;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  getSlot: async (id: string): Promise<SiSlotDetail> => {
    try {
      const res = await Axios.get(`${BASE}/slots/${id}`);
      return unwrap<SiSlotDetail>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  createSlot: async (payload: CreateSlotPayload) => {
    try {
      const res = await Axios.post(`${BASE}/slots`, payload);
      return unwrap(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  setRiderStatus: async (
    slotId: string,
    riderId: string,
    payload: { status: SiConfirmationStatus; reason?: string }
  ): Promise<{ rider: SiSlotRider }> => {
    try {
      const res = await Axios.patch(
        `${BASE}/slots/${slotId}/riders/${riderId}/status`,
        payload
      );
      return unwrap<{ rider: SiSlotRider }>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  createSchedule: async (payload: CreateSchedulePayload) => {
    try {
      const res = await Axios.post(`${BASE}/schedules`, payload);
      return unwrap(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  getSchedule: async (id: string): Promise<SiScheduleDetail> => {
    try {
      const res = await Axios.get(`${BASE}/schedules/${id}`);
      return unwrap<SiScheduleDetail>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  updateSchedule: async (
    id: string,
    payload: UpdateSchedulePayload
  ): Promise<{
    schedule: SiSchedule;
    updatedSlots: number;
    removedSlots: number;
    rosterAdded: number;
    rosterRemoved: number;
  }> => {
    try {
      const res = await Axios.patch(`${BASE}/schedules/${id}`, payload);
      return unwrap(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  updateSlot: async (
    id: string,
    payload: UpdateSlotPayload
  ): Promise<SiSlotDetail> => {
    try {
      const res = await Axios.patch(`${BASE}/slots/${id}`, payload);
      return unwrap<SiSlotDetail>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  deleteSlot: async (id: string) => {
    try {
      const res = await Axios.delete(`${BASE}/slots/${id}`);
      return unwrap(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  standbyCheck: async (
    slotId: string,
    riderCodes: string[]
  ): Promise<SiStandbyAvailability[]> => {
    try {
      const res = await Axios.post(`${BASE}/slots/${slotId}/standby-check`, {
        riderCodes,
      });
      return unwrap<{ results: SiStandbyAvailability[] }>(res.data).results;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  // Manual per-rider WA reminder (bypasses the T-12/T-2 timing gates). Returns
  // inert=true when notifications are disabled/unconfigured on the backend.
  sendRiderReminder: async (
    slotId: string,
    riderId: string
  ): Promise<{ sent: boolean; inert: boolean; providerMessageId: string | null }> => {
    try {
      const res = await Axios.post(
        `${BASE}/slots/${slotId}/riders/${riderId}/send-reminder`
      );
      return unwrap<{
        sent: boolean;
        inert: boolean;
        providerMessageId: string | null;
      }>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  replaceRider: async (
    slotId: string,
    riderId: string,
    payload: { riderCode: string; riderName: string; riderPhone: string }
  ): Promise<SiSlotDetail> => {
    try {
      const res = await Axios.post(
        `${BASE}/slots/${slotId}/riders/${riderId}/replace`,
        payload
      );
      return unwrap<SiSlotDetail>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  listSchedules: async (params?: {
    client?: string;
    activeOnly?: boolean;
  }): Promise<{
    schedules: SiSchedule[];
    rosterSummaries: SiScheduleRosterSummary[];
  }> => {
    try {
      const res = await Axios.get(`${BASE}/schedules`, { params });
      const data = unwrap<{
        schedules: SiSchedule[];
        rosterSummaries?: SiScheduleRosterSummary[];
      }>(res.data);
      return {
        schedules: data.schedules,
        rosterSummaries: data.rosterSummaries ?? [],
      };
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  deactivateSchedule: async (
    id: string,
    payload: { effectiveDate: string; reason: string }
  ) => {
    try {
      const res = await Axios.patch(`${BASE}/schedules/${id}/deactivate`, payload);
      return unwrap(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  reactivateSchedule: async (id: string) => {
    try {
      const res = await Axios.patch(`${BASE}/schedules/${id}/reactivate`);
      return unwrap(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  deleteSchedule: async (id: string) => {
    try {
      const res = await Axios.delete(`${BASE}/schedules/${id}`);
      return unwrap(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  listDayOffs: async (): Promise<SiRiderDayOff[]> => {
    try {
      const res = await Axios.get(`${BASE}/day-offs`);
      return unwrap<{ dayOffs: SiRiderDayOff[] }>(res.data).dayOffs;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  getDayOff: async (code: string): Promise<SiRiderDayOff | null> => {
    try {
      const res = await Axios.get(`${BASE}/riders/${code}/day-off`);
      return unwrap<{ dayOff: SiRiderDayOff | null }>(res.data).dayOff;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  setDayOff: async (
    code: string,
    payload: {
      riderName?: string;
      recurringWeekdays?: number[];
      specificDates?: string[];
    }
  ): Promise<SiRiderDayOff> => {
    try {
      const res = await Axios.patch(`${BASE}/riders/${code}/day-off`, payload);
      return unwrap<SiRiderDayOff>(res.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },
};
